import { supabase } from '@/lib/supabase';
import type { ProjectSite, CreateProjectSiteInput } from '../types';

/**
 * Update the primary project site address
 * Note: A primary site is auto-created by database trigger when a project is created.
 * This function updates that existing site with a custom address.
 */
export async function updatePrimaryProjectSiteAddress(
  projectId: string,
  address: {
    street_address?: string | null;
    unit?: string | null;
    city?: string | null;
    province?: string | null;
    postal_code?: string | null;
    country?: string | null;
  }
): Promise<ProjectSite> {
  const { data, error } = await supabase
    .from('project_sites')
    .update({
      street_address: address.street_address,
      unit: address.unit,
      city: address.city,
      province: address.province,
      postal_code: address.postal_code,
      country: address.country,
    })
    .eq('project_id', projectId)
    .eq('is_primary', true)
    .select()
    .single();

  if (error) {
    console.error('[projectSitesApi] Error updating project site:', error);
    throw new Error(error.message);
  }

  return data;
}

/**
 * Create a project site (for additional non-primary sites)
 */
export async function createProjectSite(
  input: CreateProjectSiteInput
): Promise<ProjectSite> {
  const { data, error } = await supabase
    .from('project_sites')
    .insert({
      business_id: input.business_id,
      project_id: input.project_id,
      site_name: input.site_name || null,
      is_primary: input.is_primary ?? false, // Default to non-primary since primary is auto-created
      street_address: input.street_address || null,
      unit: input.unit || null,
      city: input.city || null,
      province: input.province || null,
      postal_code: input.postal_code || null,
      country: input.country || 'Canada',
    })
    .select()
    .single();

  if (error) {
    console.error('[projectSitesApi] Error creating project site:', error);
    throw new Error(error.message);
  }

  return data;
}

/**
 * Get project sites for a project
 */
export async function fetchProjectSites(
  projectId: string
): Promise<ProjectSite[]> {
  const { data, error } = await supabase
    .from('project_sites')
    .select('*')
    .eq('project_id', projectId)
    .order('is_primary', { ascending: false });

  if (error) {
    console.error('[projectSitesApi] Error fetching project sites:', error);
    throw new Error(error.message);
  }

  return data || [];
}

/**
 * Get primary project site for a project
 */
export async function fetchPrimaryProjectSite(
  projectId: string
): Promise<ProjectSite | null> {
  const { data, error } = await supabase
    .from('project_sites')
    .select('*')
    .eq('project_id', projectId)
    .eq('is_primary', true)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      // No rows returned
      return null;
    }
    console.error('[projectSitesApi] Error fetching primary project site:', error);
    throw new Error(error.message);
  }

  return data;
}
